import React, { useCallback, useEffect, useState } from 'react'
import { InfoWindow, Marker } from '@react-google-maps/api'
import classNames from 'classnames'
import dayjs from 'dayjs'
import timezonePlugin from 'dayjs/plugin/timezone'
import utcPlugin from 'dayjs/plugin/utc'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { SiteCodes } from 'shared-types'
import { Image } from '~/components/Image'
import RichText from '~/components/RichText/RichText'
import { SectionContainer } from '~/components/SectionContainer'
import { StoreMapItemInfoWindow } from '~/components/StoreMapItemTooltip'
import {
  StoreDetailsMapMarkerIconStyle,
  StoreDetailsRtmMapMarkerIconStyle,
} from '~/components/StoresMap'
import { formatCustomerAddress } from '~/helpers/formatCustomerAddress'
import { usePage, useUserPreferredStore } from '~/hooks'
import { Button } from '../../components'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '../../components/Typography'
import { StoreInfo } from './Components'
import styles from './StoreDetails.module.css'
import { StoreDetailsProps } from './StoreDetails.types'

dayjs.extend(utcPlugin)
dayjs.extend(timezonePlugin)

const StoresMap = dynamic(
  async () => {
    const mod = await import('~/components/StoresMap')
    return mod.StoresMap
  },
  { ssr: false }
)

export const StoreDetails = ({
  store,
  width = 'narrow',
}: StoreDetailsProps) => {
  const [storeMap, setStoreMap] = useState<google.maps.Map | null>(null)
  const [openInfoWindow, setOpenInfoWindow] = useState(true)
  const { setPreferredStoreByLatLong } = useUserPreferredStore()
  const handleMapInstanceLoaded = useCallback((googleMap: google.maps.Map) => {
    if (googleMap) {
      setStoreMap(googleMap)
    }
  }, [])
  const page = usePage()
  const { replace, asPath } = useRouter()
  const isRtm = page?.siteCode === SiteCodes.RTM
  const brandName = isRtm ? page?.fullBrandName : page?.shortBrandName
  const isOpenStore = store?.isOpen || false

  useEffect(() => {
    let intervalId

    const checkStoreStatus = () => {
      const currentTime = dayjs().tz(store?.timezone)
      if (!store?.nextOpenOrCloseUnixMillis) {
        return
      }

      if (currentTime.valueOf() >= store.nextOpenOrCloseUnixMillis) {
        if (intervalId) {
          clearInterval(intervalId)
        }
        replace(asPath)
      } else {
        const timeUntilUpdate =
          store.nextOpenOrCloseUnixMillis - currentTime.valueOf()

        intervalId = setInterval(() => {
          checkStoreStatus()
        }, timeUntilUpdate)
      }
    }
    if (store?.nextOpenOrCloseTime) {
      checkStoreStatus()
    }

    return () => {
      clearInterval(intervalId)
    }
  }, [
    asPath,
    replace,
    store.nextOpenOrCloseUnixMillis,
    store?.nextOpenOrCloseTime,
    store?.timezone,
  ])

  if (!store) {
    return null
  }

  const {
    storeImage,
    displayName,
    storeContent,
    address,
    latitude,
    longitude,
    nextOpenOrCloseTime,
    googlePlaceId,
    reseller,
    storeLocationImage,
    storeDescription,
  } = store

  const defaultZoom = 17

  const destination = encodeURIComponent(formatCustomerAddress(address))

  const directionUrl = `https://www.google.com/maps/search/?api=1&query=${destination}&query_place_id=${googlePlaceId}`

  const handleSetAsMystore = () => {
    if (!latitude || !longitude) {
      return
    }

    setPreferredStoreByLatLong?.({ latitude, longitude }, 1)
  }

  const markerIconStyle = isRtm
    ? StoreDetailsRtmMapMarkerIconStyle
    : StoreDetailsMapMarkerIconStyle

  return (
    <SectionContainer
      width={width}
      wrapperClassName='pb-10'
      innerClassName='w-full'>
      <div className='grid 3xl:grid-cols-8 2xl:grid-cols-10 grid-cols-12 gap-4 items-center'>
        <div className='3xl:col-span-3 2xl:col-span-3 md:col-span-4 col-span-full flex flex-col gap-4'>
          {!!storeImage?.src && (
            <div className='relative'>
              <Image
                src={storeImage.src}
                width={storeImage.width}
                height={storeImage.height}
                alt={storeImage.altText}
              />
            </div>
          )}
          <div>
            {reseller && (
              <div className='py-2'>
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodySmallBold}
                  className='text-state-secondary'>
                  RESELLER
                </Typography>
              </div>
            )}
            {displayName && (
              <Typography
                variant={TypographyVariant.Heading4}
                tag={TypographyTag.h1}>
                {brandName} {displayName}
              </Typography>
            )}
          </div>
          {storeContent && (
            <Typography
              className='text-secondary-muted'
              variant={TypographyVariant.BodyRegularExtraLineHeight}
              tag={TypographyTag.div}>
              <RichText content={storeContent.json} />
            </Typography>
          )}
          {nextOpenOrCloseTime && (
            <div className='flex gap-2 items-center'>
              <Typography
                variant={TypographyVariant.BodyRegularBold}
                className={
                  isOpenStore ? styles.storeStatus : 'text-state-error'
                }
                tag={TypographyTag.span}>
                {isOpenStore ? 'Open' : 'Closed'}
              </Typography>
              <span className='text-grey-900 font-extrabold'>·</span>
              <Typography
                variant={TypographyVariant.BodyRegular}
                tag={TypographyTag.span}
                className='text-grey-900'>
                until {nextOpenOrCloseTime}
              </Typography>
            </div>
          )}
          <div className='mt-4 flex gap-2 flex-wrap'>
            <span>
              <Button
                href={directionUrl}
                target='_blank'
                status='primary'
                size='md'
                theme={{ root: styles.storeButton }}>
                <Typography
                  tag={TypographyTag.span}
                  variant={TypographyVariant.BodyRegularBold}>
                  Get Directions
                </Typography>
              </Button>
            </span>
            <span>
              {!reseller && (
                <Button
                  status='secondary'
                  size='md'
                  theme={{ root: styles.storeButton }}
                  onClick={handleSetAsMystore}>
                  <Typography
                    tag={TypographyTag.span}
                    variant={TypographyVariant.BodyRegularBold}
                    className='!text-primary-heading'>
                    Set as my store
                  </Typography>
                </Button>
              )}
            </span>
          </div>
        </div>
        <div
          className={classNames(
            '3xl:col-span-5 2xl:col-span-7 md:col-span-8 col-span-full aspect-w-5 md:!w-full md:!h-full aspect-h-3 md:!ml-auto md:!left-auto mt-6 md:mt-0',
            styles.fullWidth
          )}>
          <StoresMap
            center={{ latitude, longitude }}
            initialZoom={defaultZoom}
            onLoadedMap={handleMapInstanceLoaded}>
            {storeMap && (
              <Marker
                position={{
                  lat: latitude,
                  lng: longitude,
                }}
                options={{
                  icon: {
                    ...markerIconStyle,
                    size: new google.maps.Size(
                      markerIconStyle.width,
                      markerIconStyle.height
                    ),
                    anchor: new google.maps.Point(
                      markerIconStyle.width * Number('0.5'),
                      markerIconStyle.height * Number('0.5')
                    ),
                  },
                }}
                onClick={() => {
                  return setOpenInfoWindow(true)
                }}>
                {openInfoWindow && (
                  <div>
                    {/* @ts-ignore */}
                    <InfoWindow
                      position={{
                        lat: latitude,
                        lng: longitude,
                      }}>
                      <StoreMapItemInfoWindow
                        store={store}
                        onClose={() => {
                          return setOpenInfoWindow(false)
                        }}
                      />
                    </InfoWindow>
                  </div>
                )}
              </Marker>
            )}
          </StoresMap>
        </div>
      </div>
      <StoreInfo store={store} />
      {!!storeLocationImage?.src && (
        <div className='mb-10'>
          <Image
            src={storeLocationImage.src}
            width={storeLocationImage.width}
            height={storeLocationImage.height}
            alt={storeLocationImage.altText}
          />
        </div>
      )}
      {storeDescription && (
        <div
          className={classNames('pt-12 border-t', {
            [styles.seoContainer]: isRtm,
          })}>
          <Typography
            variant={TypographyVariant.BodyRegularExtraLineHeight}
            tag={TypographyTag.p}
            className={!isRtm && 'text-justify'}>
            {storeDescription}
          </Typography>
        </div>
      )}
    </SectionContainer>
  )
}
